import {
    WORKBOOK_FIELD_CATALOG,
    DEFAULT_UNPLACED_WORKBOOK_FIELD_IDS,
    GUARANTOR_DEPENDENT_FIELD_IDS,
    GUARANTOR_FIELDS_SHOW_IF,
    DEFAULT_REQUIRED_WORKBOOK_FIELD_IDS,
    PINNED_WORKBOOK_FIELD_IDS,
    PET_HAS_PETS_DEPENDENT_FIELD_IDS,
    VEHICLE_DEPENDENT_FIELD_IDS,
    VEHICLE_FIELDS_SHOW_IF,
    catalogFieldsForBucket,
} from '../ApplicationWorkbook/workbookCatalog'
import {
    GENDER_FIELD_ID,
    GENDER_QUESTION_BY_MODE,
    GenderQuestionMode,
    genderQuestionSpec,
    normalizeGenderQuestionMode,
} from '../ApplicationWorkbook/genderQuestion'
import type {
    CustomFieldInputType,
    PlatformWorkbookBucketId,
    WorkbookSectionKind,
} from '../ApplicationWorkbook/workbook.types'

import type { QualifyingMethodId } from '../LeasingSeasonConfig/conditionalApprovals'
import {
    FALLBACK_QUALIFYING_METHOD_FIELD_ID,
    QUALIFYING_METHOD_FIELD_ID,
    QUALIFYING_METHOD_IDS,
    QUALIFYING_METHOD_META,
} from '../LeasingSeasonConfig/conditionalApprovals'
import {
    MAX_CUSTOM_SECTIONS,
    WORKBOOK_BUCKETS,
    WorkbookBucketId,
    isCustomSectionId,
    isPlatformBucketId,
    platformBucketLabel,
    platformBucketSummary,
} from './workbookBuckets'

export type { WorkbookBucketId }
export { MAX_CUSTOM_SECTIONS }

/** Show this question only when another answer matches (effect is always show). */
export interface FieldShowIf {
    depends_on_field_id: string
    /** Answer value that reveals this question (e.g. "Yes"). */
    equals: string
}

export interface WorkbookSectionFieldDraft {
    field_id: string
    question_text: string
    input_type: string
    enabled: boolean
    required: boolean
    /** True for user-authored questions (not EZOS catalog). */
    is_custom?: boolean
    /** Must remain in the workbook — hide remove. */
    pinned?: boolean
    /**
     * Registry id when this draft was loaded from EzosWorkbookField.
     * Kept if the admin renames ``field_id`` so saves can PATCH the original row.
     */
    source_field_id?: string
    /** Fixed choices for select / boolean-style answers. */
    options?: string[]
    /** When set, question is indented under the trigger and shown only if it matches. */
    show_if?: FieldShowIf
    /** Identity gender: government-ID marker vs self-identified. */
    gender_question_mode?: GenderQuestionMode
}

export interface SectionFieldDisplayRow {
    field: WorkbookSectionFieldDraft
    /** Index in the section `fields` array. */
    arrayIndex: number
    /** 0 = root, 1+ = contingent under a parent. */
    depth: number
    /** 1-based display order. */
    number: number
}

export interface WorkbookSectionDraft {
    bucket_id: WorkbookBucketId
    label: string
    /** Short description of the questions in this section. */
    summary: string
    kind: WorkbookSectionKind
    /** True after the user saves the section configure modal. */
    configured: boolean
    /**
     * Platform sections may be disabled (e.g. Failed Qualification when the
     * property manager chooses after-fail methods). Defaults to true.
     */
    enabled?: boolean
    fields: WorkbookSectionFieldDraft[]
}

/** Keyed by section id (platform or custom_*). */
export type WorkbookSectionsState = Partial<
    Record<string, WorkbookSectionDraft>
>

const DEFAULT_SELECT_OPTIONS: Record<string, string[]> = {
    current_address_state: [
        'AL',
        'AK',
        'AZ',
        'AR',
        'CA',
        'CO',
        'CT',
        'DE',
        'FL',
        'GA',
        'HI',
        'ID',
        'IL',
        'IN',
        'IA',
        'KS',
        'KY',
        'LA',
        'ME',
        'MD',
        'MA',
        'MI',
        'MN',
        'MS',
        'MO',
        'MT',
        'NE',
        'NV',
        'NH',
        'NJ',
        'NM',
        'NY',
        'NC',
        'ND',
        'OH',
        'OK',
        'OR',
        'PA',
        'RI',
        'SC',
        'SD',
        'TN',
        'TX',
        'UT',
        'VT',
        'VA',
        'WA',
        'WV',
        'WI',
        'WY',
        'DC',
    ],
    lease_term: ['9 months', '12 months', 'Academic year', 'Other'],
    floor_plan_preference: ['Studio', '1 bedroom', '2 bedroom', '3+ bedroom'],
    graduation_year: ['2026', '2027', '2028', '2029', '2030', 'Other'],
    qualifying_method: [
        'Self qualified',
        'Personal guarantor',
        'Leap / GWP',
        'Enhanced security deposit',
        'Rent prepayment',
    ],
    fallback_qualifying_method: [
        'Leap / GWP',
        'Enhanced security deposit',
        'Rent prepayment',
    ],
    guarantor_relationship: [
        'Parent',
        'Guardian',
        'Relative',
        'Spouse / partner',
        'Other',
    ],
    renters_insurance: ['I will purchase', 'I already have', 'Not sure'],
    study_habits: ['Quiet / focused', 'Mixed', 'Social study'],
    sleep_schedule: ['Early sleeper', 'Average', 'Night owl'],
    cleanliness: ['Very tidy', 'Average', 'Relaxed'],
    noise_tolerance: ['Prefers quiet', 'Average', 'Tolerates noise'],
    social_preference: ['Mostly private', 'Balanced', 'Very social'],
    academic_focus: ['STEM', 'Business', 'Arts / humanities', 'Other'],
    pet_animal_type: ['dog', 'cat', 'fish', 'reptile', 'rodent', 'bird'],
    [GENDER_FIELD_ID]: [...GENDER_QUESTION_BY_MODE.id_marker.options],
}

DEFAULT_SELECT_OPTIONS.vehicle_license_plate_state = [
    ...DEFAULT_SELECT_OPTIONS.current_address_state,
]

const BOOLEAN_OPTIONS = ['Yes', 'No']

export const CUSTOM_QUESTION_INPUT_TYPES: {
    value: CustomFieldInputType
    label: string
}[] = [
    { value: 'text', label: 'Short text' },
    { value: 'textarea', label: 'Long text' },
    { value: 'select', label: 'Dropdown' },
    { value: 'boolean', label: 'Yes / No' },
    { value: 'date', label: 'Date' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'address', label: 'Address' },
]

const EXTRA_INPUT_TYPE_LABELS: Record<string, string> = {
    premiums: 'Premiums',
    roommates: 'Roommate cards',
    ssn: 'SSN',
    currency: 'Currency',
    file: 'File upload',
}

/** Choices come from this property, not catalog seed strings. */
export const LIVE_PROPERTY_CHOICE_FIELD_IDS = new Set([
    'floor_plan_preference',
    'preferred_premiums',
    'preferred_roommates',
    'pet_animal_type',
    'pet_breed',
])

export function inputTypeLabel(value: string): string {
    return (
        CUSTOM_QUESTION_INPUT_TYPES.find((t) => t.value === value)?.label ??
        EXTRA_INPUT_TYPE_LABELS[value] ??
        value
    )
}

/** Turn a short catalog label into a tenant-facing question. */
export function defaultQuestionText(label: string, inputType: string): string {
    const trimmed = label.trim()
    if (!trimmed) return 'Please answer this question.'
    if (/[?]$/.test(trimmed)) return trimmed
    if (inputType === 'boolean') {
        return trimmed.endsWith('?') ? trimmed : `${trimmed}?`
    }
    if (/^(has|may|is|are|do|does)\b/i.test(trimmed)) {
        return trimmed.endsWith('?') ? trimmed : `${trimmed}?`
    }
    return `What is your ${trimmed.charAt(0).toLowerCase()}${trimmed.slice(1)}?`
}

export function defaultOptionsForField(
    fieldId: string,
    inputType: string,
): string[] | undefined {
    if (inputType === 'boolean') return [...BOOLEAN_OPTIONS]
    if (inputType === 'select') {
        return DEFAULT_SELECT_OPTIONS[fieldId]
            ? [...DEFAULT_SELECT_OPTIONS[fieldId]]
            : ['Option 1', 'Option 2']
    }
    return undefined
}

function newCustomId(prefix: string): string {
    const rand =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
            : `${Date.now().toString(36)}${Math.random()
                  .toString(36)
                  .slice(2, 8)}`
    return `${prefix}${rand}`
}

export function buildDraftFieldsForBucket(
    bucketId: WorkbookBucketId,
): WorkbookSectionFieldDraft[] {
    if (!isPlatformBucketId(bucketId)) return []
    return catalogFieldsForBucket(bucketId)
        .filter((field) => !DEFAULT_UNPLACED_WORKBOOK_FIELD_IDS.has(field.id))
        .map((field) => {
            const options = defaultOptionsForField(field.id, field.input_type)
            const questionAlready =
                /[?.]$/.test(field.label.trim()) || field.label.includes('?')
            return {
                field_id: field.id,
                question_text: questionAlready
                    ? field.label
                    : defaultQuestionText(field.label, field.input_type),
                input_type: field.input_type,
                enabled: true,
                required:
                    PINNED_WORKBOOK_FIELD_IDS.has(field.id) ||
                    DEFAULT_REQUIRED_WORKBOOK_FIELD_IDS.has(field.id) ||
                    bucketId === 'failed_qualification',
                pinned: PINNED_WORKBOOK_FIELD_IDS.has(field.id),
                ...(options ? { options } : {}),
                ...(field.id === GENDER_FIELD_ID
                    ? genderQuestionSpec('id_marker')
                    : {}),
                ...(VEHICLE_DEPENDENT_FIELD_IDS.has(field.id)
                    ? { show_if: { ...VEHICLE_FIELDS_SHOW_IF } }
                    : {}),
                ...(PET_HAS_PETS_DEPENDENT_FIELD_IDS.has(field.id)
                    ? {
                          show_if: {
                              depends_on_field_id: 'has_pets',
                              equals: 'Yes',
                          },
                      }
                    : {}),
                ...(field.id === 'pet_breed'
                    ? {
                          show_if: {
                              depends_on_field_id: 'pet_animal_type',
                              equals: 'dog',
                          },
                      }
                    : {}),
                ...(GUARANTOR_DEPENDENT_FIELD_IDS.has(field.id)
                    ? { show_if: { ...GUARANTOR_FIELDS_SHOW_IF } }
                    : {}),
            }
        })
}

/** Floor plan must be configured before primary Screening (rent multiple routing). */
export function isFloorPlanPreferenceConfigured(
    sections: WorkbookSectionsState | undefined,
): boolean {
    const lease = listWorkbookSections(sections).find(
        (s) => s.bucket_id === 'lease_preferences',
    )
    if (!lease?.configured) return false
    const field = lease.fields.find(
        (item) => item.field_id === 'floor_plan_preference',
    )
    return Boolean(field && field.enabled)
}

export function createSectionDraft(
    bucketId: WorkbookBucketId,
    configured = false,
    overrides?: Partial<
        Pick<WorkbookSectionDraft, 'label' | 'kind' | 'summary'>
    >,
): WorkbookSectionDraft {
    const kind: WorkbookSectionKind =
        overrides?.kind ??
        (isCustomSectionId(String(bucketId)) ? 'custom' : 'platform')
    const label =
        overrides?.label ??
        (kind === 'platform'
            ? platformBucketLabel(String(bucketId))
            : 'Custom section')
    const summary =
        overrides?.summary ??
        (kind === 'platform' ? platformBucketSummary(String(bucketId)) : '')
    return {
        bucket_id: bucketId,
        label,
        summary,
        kind,
        configured,
        enabled: true,
        fields: kind === 'platform' ? buildDraftFieldsForBucket(bucketId) : [],
    }
}

export function listWorkbookSections(
    sections: WorkbookSectionsState | undefined,
): WorkbookSectionDraft[] {
    const state = sections ?? {}
    const result: WorkbookSectionDraft[] = []

    for (const bucket of WORKBOOK_BUCKETS) {
        const draft = getWorkbookSectionDraft(state, bucket.id)
        // Hide Failed Qualification when disabled (PM chooses after-fail methods).
        if (bucket.id === 'failed_qualification' && draft.enabled === false) {
            continue
        }
        result.push(draft)
    }

    const custom = Object.values(state)
        .filter(
            (draft): draft is WorkbookSectionDraft =>
                !!draft && draft.kind === 'custom',
        )
        .sort((a, b) => a.label.localeCompare(b.label))
    result.push(...custom)
    return result
}

export function countCustomSections(
    sections: WorkbookSectionsState | undefined,
): number {
    return listWorkbookSections(sections).filter((s) => s.kind === 'custom')
        .length
}

export function isWorkbookSectionConfigured(
    sections: WorkbookSectionsState | undefined,
    bucketId: WorkbookBucketId,
): boolean {
    return sections?.[bucketId]?.configured === true
}

export function getWorkbookSectionDraft(
    sections: WorkbookSectionsState | undefined,
    bucketId: WorkbookBucketId,
): WorkbookSectionDraft {
    const existing = sections?.[bucketId]
    if (existing) {
        const kind: WorkbookSectionKind =
            existing.kind ??
            (isCustomSectionId(String(bucketId)) ? 'custom' : 'platform')
        return {
            ...existing,
            label:
                existing.label ||
                (kind === 'platform'
                    ? platformBucketLabel(String(bucketId))
                    : 'Custom section'),
            summary:
                existing.summary ||
                (kind === 'platform'
                    ? platformBucketSummary(String(bucketId))
                    : ''),
            kind,
            enabled: existing.enabled !== false,
        }
    }
    return createSectionDraft(bucketId, false)
}

export function upsertWorkbookSection(
    sections: WorkbookSectionsState | undefined,
    draft: WorkbookSectionDraft,
): WorkbookSectionsState {
    return {
        ...(sections ?? {}),
        [draft.bucket_id]: {
            ...draft,
            configured: true,
            fields: draft.fields.map((field) => ({
                ...field,
                options: field.options ? [...field.options] : undefined,
            })),
        },
    }
}

export function workbookBucketLabel(
    bucketId: WorkbookBucketId,
    sections?: WorkbookSectionsState,
): string {
    const draft = sections?.[bucketId]
    if (draft?.label) return draft.label
    if (isPlatformBucketId(String(bucketId))) {
        return platformBucketLabel(String(bucketId))
    }
    return String(bucketId)
}

export function inputTypeNeedsOptions(inputType: string): boolean {
    return inputType === 'select' || inputType === 'boolean'
}

export function addCustomSection(
    sections: WorkbookSectionsState | undefined,
    label = 'Custom section',
): WorkbookSectionsState | null {
    if (countCustomSections(sections) >= MAX_CUSTOM_SECTIONS) {
        return null
    }
    const id = newCustomId('custom_')
    const draft = createSectionDraft(id, true, {
        kind: 'custom',
        label: label.trim() || 'Custom section',
    })
    return upsertWorkbookSection(sections, draft)
}

export function removeCustomSection(
    sections: WorkbookSectionsState | undefined,
    bucketId: WorkbookBucketId,
): WorkbookSectionsState {
    if (!isCustomSectionId(String(bucketId))) {
        return sections ?? {}
    }
    const next = { ...(sections ?? {}) }
    delete next[bucketId]
    return next
}

export function moveFieldBetweenSections(
    sections: WorkbookSectionsState | undefined,
    fieldId: string,
    fromSectionId: WorkbookBucketId,
    toSectionId: WorkbookBucketId,
): WorkbookSectionsState {
    if (fromSectionId === toSectionId) return sections ?? {}
    const from = getWorkbookSectionDraft(sections, fromSectionId)
    const to = getWorkbookSectionDraft(sections, toSectionId)
    const field = from.fields.find((f) => f.field_id === fieldId)
    if (!field) return sections ?? {}
    if (to.fields.some((f) => f.field_id === fieldId)) return sections ?? {}

    return {
        ...(sections ?? {}),
        [fromSectionId]: {
            ...from,
            fields: from.fields.filter((f) => f.field_id !== fieldId),
        },
        [toSectionId]: {
            ...to,
            fields: [...to.fields, { ...field }],
        },
    }
}

export function createCustomFieldDraft(
    questionText: string,
    inputType: CustomFieldInputType,
    options?: string[],
    fieldId?: string,
): WorkbookSectionFieldDraft {
    const opts =
        options ??
        (inputTypeNeedsOptions(inputType)
            ? defaultOptionsForField('__custom__', inputType)
            : undefined)
    return {
        field_id: fieldId?.trim() || newCustomId('custom_'),
        question_text: questionText.trim() || 'Custom question',
        input_type: inputType,
        enabled: true,
        required: false,
        is_custom: true,
        ...(opts ? { options: opts } : {}),
    }
}

export function addCustomFieldToSection(
    sections: WorkbookSectionsState | undefined,
    sectionId: WorkbookBucketId,
    field: WorkbookSectionFieldDraft,
): WorkbookSectionsState {
    const draft = getWorkbookSectionDraft(sections, sectionId)
    return upsertWorkbookSection(sections, {
        ...draft,
        fields: [...draft.fields, field],
    })
}

/** Catalog field ids — used to reject unknown drafts. */
export const KNOWN_WORKBOOK_FIELD_IDS = new Set(
    WORKBOOK_FIELD_CATALOG.map((field) => field.id),
)

export function isKnownOrCustomFieldId(fieldId: string): boolean {
    if (KNOWN_WORKBOOK_FIELD_IDS.has(fieldId)) return true
    // Org registry customs: slug ids (allergies) or legacy custom_<rand>.
    return (
        /^[a-z][a-z0-9_]{1,55}$/.test(fieldId) ||
        (fieldId.startsWith('custom_') && fieldId.length > 7)
    )
}

function isQualifyingMethodTrigger(fieldId: string): boolean {
    return (
        fieldId === QUALIFYING_METHOD_FIELD_ID ||
        fieldId === FALLBACK_QUALIFYING_METHOD_FIELD_ID
    )
}

function slugifyAnswer(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/[\s/]+/g, '_')
}

function asQualifyingMethodId(value: string): QualifyingMethodId | null {
    const raw = value.trim()
    if ((QUALIFYING_METHOD_IDS as string[]).includes(raw)) {
        return raw as QualifyingMethodId
    }
    const byLabel = QUALIFYING_METHOD_IDS.find(
        (id) =>
            QUALIFYING_METHOD_META[id].label.toLowerCase() ===
            raw.toLowerCase(),
    )
    return byLabel ?? null
}

export function canBeContingencyTrigger(
    field: WorkbookSectionFieldDraft,
): boolean {
    if (isQualifyingMethodTrigger(field.field_id)) return true
    return (
        field.input_type === 'boolean' ||
        (field.input_type === 'select' && (field.options?.length ?? 0) > 0)
    )
}

export function answerChoicesForTrigger(
    field: WorkbookSectionFieldDraft,
): string[] {
    if (isQualifyingMethodTrigger(field.field_id)) {
        const fromOptions = (field.options || [])
            .map((option) => asQualifyingMethodId(option))
            .filter((id): id is QualifyingMethodId => Boolean(id))
        if (fromOptions.length) {
            return Array.from(new Set(fromOptions))
        }
        return [...QUALIFYING_METHOD_IDS]
    }
    if (field.input_type === 'boolean') {
        return field.options?.length ? [...field.options] : ['Yes', 'No']
    }
    if (field.input_type === 'select' && field.options?.length) {
        return [...field.options]
    }
    return []
}

export function displayAnswerForShowIf(
    parent: WorkbookSectionFieldDraft | undefined,
    equals: string,
): string {
    const raw = (equals || '').trim()
    if (!raw) return ''
    if (parent && isQualifyingMethodTrigger(parent.field_id)) {
        const methodId = asQualifyingMethodId(raw)
        if (methodId) return QUALIFYING_METHOD_META[methodId].label
    }
    const options = parent?.options || []
    const exact = options.find((option) => option === raw)
    if (exact) return exact
    const slug = slugifyAnswer(raw)
    const bySlug = options.find((option) => slugifyAnswer(option) === slug)
    if (bySlug) return bySlug
    if (raw.includes('_')) {
        const [first, ...rest] = raw.split('_').filter(Boolean)
        if (!first) return raw
        return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(
            ' ',
        )
    }
    return raw
}

export function storedAnswerForShowIf(
    parent: WorkbookSectionFieldDraft | undefined,
    equals: string,
): string {
    const raw = (equals || '').trim()
    if (!raw || !parent) return raw
    const choices = answerChoicesForTrigger(parent)
    if (choices.includes(raw)) return raw
    const byLabel = choices.find(
        (choice) =>
            displayAnswerForShowIf(parent, choice).toLowerCase() ===
            raw.toLowerCase(),
    )
    return byLabel ?? raw
}

export function contingencyTooltipBody(
    parent: WorkbookSectionFieldDraft | undefined,
    equals: string,
    parentNumber?: number,
): string {
    const answer = displayAnswerForShowIf(parent, equals)
    const question = parentNumber
        ? `question number ${parentNumber}`
        : 'another question'
    if (!answer) {
        return `This question only appears when the answer to ${question} matches.`
    }
    return `This question only appears when the answer to ${question} is equal to ${answer}.`
}

function parentKey(field: WorkbookSectionFieldDraft): string | null {
    return field.show_if?.depends_on_field_id ?? null
}

/** Visual order: parents first, contingent children nested immediately after. */
export function buildSectionFieldDisplayRows(
    fields: WorkbookSectionFieldDraft[],
): SectionFieldDisplayRow[] {
    const idSet = new Set(fields.map((f) => f.field_id))
    const indexById = new Map(fields.map((f, i) => [f.field_id, i]))
    const isChild = (f: WorkbookSectionFieldDraft) =>
        Boolean(f.show_if && idSet.has(f.show_if.depends_on_field_id))

    const childrenOf = (parentId: string) =>
        fields.filter((f) => f.show_if?.depends_on_field_id === parentId)

    const rows: Omit<SectionFieldDisplayRow, 'number'>[] = []
    const emitted = new Set<string>()

    const emit = (field: WorkbookSectionFieldDraft, depth: number) => {
        if (emitted.has(field.field_id)) return
        emitted.add(field.field_id)
        rows.push({
            field,
            arrayIndex: indexById.get(field.field_id) ?? 0,
            depth,
        })
        for (const child of childrenOf(field.field_id)) {
            emit(child, depth + 1)
        }
    }

    for (const field of fields) {
        if (emitted.has(field.field_id)) continue
        if (isChild(field)) continue
        emit(field, 0)
    }
    for (const field of fields) {
        if (!emitted.has(field.field_id)) {
            emit(field, isChild(field) ? 1 : 0)
        }
    }

    return rows.map((row, i) => ({ ...row, number: i + 1 }))
}

/** Swap a field with the previous/next sibling (same contingency parent). */
export function moveFieldAmongSiblings(
    fields: WorkbookSectionFieldDraft[],
    fieldId: string,
    direction: 'up' | 'down',
): WorkbookSectionFieldDraft[] {
    const field = fields.find((f) => f.field_id === fieldId)
    if (!field) return fields
    const key = parentKey(field)
    const siblingIds = fields
        .filter((f) => parentKey(f) === key)
        .map((f) => f.field_id)
    const sibIndex = siblingIds.indexOf(fieldId)
    if (sibIndex < 0) return fields
    const swapWith =
        direction === 'up' ? siblingIds[sibIndex - 1] : siblingIds[sibIndex + 1]
    if (!swapWith) return fields

    const a = fields.findIndex((f) => f.field_id === fieldId)
    const b = fields.findIndex((f) => f.field_id === swapWith)
    if (a < 0 || b < 0) return fields
    const next = [...fields]
    ;[next[a], next[b]] = [next[b], next[a]]
    return next
}

export function siblingMoveFlags(
    fields: WorkbookSectionFieldDraft[],
    fieldId: string,
): { canUp: boolean; canDown: boolean } {
    const field = fields.find((f) => f.field_id === fieldId)
    if (!field) return { canUp: false, canDown: false }
    const key = parentKey(field)
    const siblingIds = fields
        .filter((f) => parentKey(f) === key)
        .map((f) => f.field_id)
    const sibIndex = siblingIds.indexOf(fieldId)
    return {
        canUp: sibIndex > 0,
        canDown: sibIndex >= 0 && sibIndex < siblingIds.length - 1,
    }
}

/** Place field after its contingency parent (and after existing siblings). */
export function placeFieldAfterParent(
    fields: WorkbookSectionFieldDraft[],
    fieldId: string,
    parentId: string,
): WorkbookSectionFieldDraft[] {
    const field = fields.find((f) => f.field_id === fieldId)
    if (!field) return fields
    const without = fields.filter((f) => f.field_id !== fieldId)
    const parentIndex = without.findIndex((f) => f.field_id === parentId)
    if (parentIndex < 0) {
        return [...without, field]
    }
    let insertAt = parentIndex + 1
    while (
        insertAt < without.length &&
        without[insertAt].show_if?.depends_on_field_id === parentId
    ) {
        insertAt += 1
    }
    const next = [...without]
    next.splice(insertAt, 0, field)
    return next
}

/** Clear contingencies that pointed at a removed trigger. */
export function clearContingenciesOnRemovedTrigger(
    fields: WorkbookSectionFieldDraft[],
    removedFieldId: string,
): WorkbookSectionFieldDraft[] {
    return fields.map((f) =>
        f.show_if?.depends_on_field_id === removedFieldId
            ? { ...f, show_if: undefined }
            : f,
    )
}

export type { PlatformWorkbookBucketId }
